import type { Position, Direction } from "../types/grid.js";
import type { GridMap } from "./GridMap.js";
import { cellOccupancyStore } from "./cellOccupancyStore.js";

/** Returns the target position when moving one step in the given direction. */
export function targetPosition(current: Position, facing: Direction): Position {
  return {
    x: current.x + Math.round(Math.sin(facing)),
    z: current.z - Math.round(Math.cos(facing)),
  };
}

/** Returns the target position for a lateral strafe (left = -π/2, right = +π/2). */
export function strafePosition(current: Position, facing: Direction, side: "left" | "right"): Position {
  const offset = side === "left" ? -Math.PI / 2 : Math.PI / 2;
  const angle = facing + offset;
  return {
    x: current.x + Math.round(Math.sin(angle)),
    z: current.z - Math.round(Math.cos(angle)),
  };
}

/**
 * Returns true when the target position is legal to enter.
 *
 * Geometry first (bounds + walls), then entity collisions via
 * `cellOccupancyStore` (E16). Living enemies and NPCs hard-block the
 * player; dead enemies are removed from occupancy at death so they
 * pass through. The caller decides what to do on rejection — most
 * callers just refuse the move; the input handler additionally fires
 * an NPC bump-talk via the shared object-click dispatcher path.
 */
export function canMoveTo(grid: GridMap, target: Position): boolean {
  if (!grid.isInBounds(target) || !grid.isPassable(target)) return false;
  if (cellOccupancyStore.isOccupied(target)) return false;
  return true;
}

/** True when geometry permits the move but an entity is sitting in the cell. */
export function blockedByEntity(grid: GridMap, target: Position): boolean {
  if (!grid.isInBounds(target) || !grid.isPassable(target)) return false;
  return cellOccupancyStore.isOccupied(target);
}
